import { inject, Injectable } from '@angular/core';
import { IMyDevice, SetPresence } from 'matrix-js-sdk';
import { Account } from '../../../shared-api/account';
import { AuthorizationMode } from '../../../shared-api/authorizationMode';
import { Rooms } from '../../../shared-api/rooms';
import { AccountService } from '../account/account.service';
import { AuthorizationManagementService } from '../authorization-management/authorization-management.service';
import { MatrixService } from '../matrix/matrix.service';
import { PresenceService } from '../presence/presence.service';
import { RoomService } from '../room/room.service';
import { DEFAULT_CONFIGURATION, SettingsService } from '../settings/settings.service';

/** Display-Name, mit dem der Server Dehydrated Devices markiert — die dürfen nicht gelöscht werden. */
const DEHYDRATED_DEVICE_DISPLAY_NAME = 'Dehydrated device';

/**
 * Phantom-Cleanup-Service für E2E-Tests: setzt Einstellungen zurück,
 * erzwingt Presence-Status "offline", hebt Blockierungen auf und verlässt
 * alle Räume. Wird von der {@link TestDriverBridge} via `globalThis`
 * für E2E-Tests exponiert.
 *
 * Begründung: keine AFO, rein test-infrastrukturell
 */
@Injectable({ providedIn: 'root' })
export class PhantomCleanupService {
  private readonly accountService = inject(AccountService);
  private readonly roomService = inject(RoomService);
  private readonly settingsService = inject(SettingsService);
  private readonly matrix = inject(MatrixService);
  private readonly presenceService = inject(PresenceService);
  private readonly authorizationManagement = inject(AuthorizationManagementService);

  /**
   * Führt den kompletten Cleanup-Durchlauf aus.
   * @returns Anzahl der verlassenen Räume
   */
  async cleanupUser(): Promise<number> {
    console.log('🧹 Phantom-Cleanup gestartet');
    try {
      await this.waitForClient();
      const roomCount = await this.cleanupLogic();
      console.log('✅ Phantom-Cleanup fertig');
      return roomCount;
    } catch (err) {
      console.error('❌ Phantom-Cleanup fehlgeschlagen', err);
      throw err;
    }
  }

  /**
   * Wartet, bis der Matrix-Client aktiv ist (max. 20s).
   * Der Cleanup-Hook wird am App-Root (TestDriverBridge) bereitgestellt und ist daher u.U.
   * schon verfügbar, bevor der Login abgeschlossen ist. Ohne dieses Warten würde der erste
   * Client-Zugriff in {@link cleanupLogic} "Es ist kein Matrix-Client aktiv" werfen.
   */
  private async waitForClient(timeoutMs = 20_000): Promise<void> {
    const start = Date.now();
    while (!this.matrix.hasClient) {
      if (Date.now() - start > timeoutMs) {
        throw new Error('Phantom-Cleanup: Matrix-Client wurde nicht rechtzeitig aktiv');
      }
      await new Promise(resolve => setTimeout(resolve, 100));
    }
  }

  /**
   * Erzwingt serverseitigen Presence-Status "offline" mit Retry bei 429.
   * Nutzt retry_after_ms aus der Server-Antwort für optimale Wartezeit.
   */
  async forcePresenceOffline(): Promise<void> {
    for (let attempt = 1; attempt <= 3; attempt++) {
      try {
        await this.matrix.client.setPresence({ presence: 'offline' });
        console.log('forcePresenceOffline: Erfolgreich');
        return;
      } catch (e: unknown) {
        const retryMs = Math.min((e as { data?: { retry_after_ms?: number } })?.data?.retry_after_ms || 15_000, 15_000);
        console.warn(`forcePresenceOffline Versuch ${attempt}/3 fehlgeschlagen, warte ${retryMs}ms`);
        if (attempt < 3) {
          await new Promise(resolve => setTimeout(resolve, retryMs));
        }
      }
    }
    throw new Error('forcePresenceOffline: Alle 3 Versuche fehlgeschlagen');
  }

  private async cleanupLogic(): Promise<number> {
    console.log('Reset default settings');
    const configClone = structuredClone(DEFAULT_CONFIGURATION);
    // Privatsphäre-Dialog unmittelbar nach Schlüsseleingabe nicht öffnen
    configClone.settingsConfigurations.trackingPopUp = true;
    await this.settingsService.saveSettings(this.matrix.client, configClone);

    // setPresence mit Retry, da 429 Rate-Limit häufig auftritt
    let presenceSet = false;
    for (let attempt = 1; attempt <= 3; attempt++) {
      try {
        await this.presenceService.updateCurrentUserPresence();
        presenceSet = true;
        break;
      } catch (e) {
        console.warn(`setPresence Versuch ${attempt}/3 fehlgeschlagen (vermutlich 429)`, e);
        if (attempt < 3) {
          await new Promise(resolve => setTimeout(resolve, 3000));
        }
      }
    }
    // Fallback: setSyncPresence lokal setzen, auch wenn setPresence fehlgeschlagen ist
    if (!presenceSet) {
      await this.matrix.client.setSyncPresence(SetPresence.Offline);
    }

    // Etwaige Blockierungen aufheben
    await this.authorizationManagement.setAuthorizationMode(AuthorizationMode.AllowAll);

    let username: string;
    try {
      username = ((await this.accountService.readAccount()) as Account)?.username;
      console.log(`«${username}»: Leave and forget all rooms`);
    } catch (e) {
      console.warn('Read account failed', e);
      username = '[unknown user]';
    }

    let rooms: Rooms;
    try {
      rooms = await this.roomService.getRooms();
    } catch (e) {
      console.warn('Get rooms failed', e);
      rooms = [];
    }

    for (const room of rooms) {
      try {
        // Bereits verlassener Raum wird kein zweites Mal verlassen.
        await this.roomService.leaveRoom(room.roomId);
        console.log(`${username}: Left room "${room.name}"`);
      } catch (e) {
        console.log(`${username}: Failed to leave room "${room.name}"`, e);
      }

      try {
        await this.roomService.forgetRoom(room.roomId);
        console.log(`${username}: Forgot room '${room.name}'`);
      } catch (e) {
        console.log(`${username}: Failed to forget room '${room.name}': ${e}`);
      }
    }

    return rooms.length;
  }
}
